"use client";

import { DEFAULT_CURRENCY } from "@utils/default";
import { getCookie } from "cookies-next";
import { useFormatter, useTranslations } from "next-intl";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";

import { search } from "@/actions/currencyConversion";

import useMortgageCalculator from "../../hooks/useMortgageCalculator";
import "../GuidedToolLauncher/GuidedToolLauncher.css";
import Button from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";
import MortgageCalculatorLabelInput from "./MortgageCalculatorLabelInput";
import MortgageCalculatorLoanAmountSection from "./MortgageCalculatorLoanAmountSection";

interface Props {
  entry?: any;
  projectInfo?: {
    priceMin: number;
    priceMax: number;
    country: string;
    status: string;
  };
  currency?: string;
  locale: string;
}

const allowedCountry = "United Arab Emirates";
const allowedStatus = "ready";

export default function MortgageCalculator({ entry, projectInfo, currency: currency, locale }: Props) {
  const format = useFormatter();
  const t = useTranslations();
  const currentCurrency = getCookie("currency");
  const [currentCurrencyConverstionRate, setCurrentCurrencyConverstionRate] = useState(0);
  const [previousSelectedCurrency, setPreviousSelectedCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [convertedPropertyPrice, setConvertedPropertyPrice] = useState<string | number>(t("common.loading"));
  const priceMin = useMemo(
    () =>
      format.number(Math.round(Number(projectInfo?.priceMin) / 100 || 0), {
        currency: currency,
        trailingZeroDisplay: "stripIfInteger",
      }),
    [format, projectInfo?.priceMin, currency],
  );
  const priceMax = useMemo(
    () =>
      format.number(Math.round(Number(projectInfo?.priceMax) / 100 || 0), {
        currency: currency,
        trailingZeroDisplay: "stripIfInteger",
      }),
    [format, projectInfo?.priceMax, currency],
  );

  const sanitizePriceMin = useMemo(() => priceMin.replace(/,/g, ""), [priceMin]);
  const sanitizePriceMax = useMemo(() => priceMax.replace(/,/g, ""), [priceMax]);

  const data = useMortgageCalculator({
    constants: entry?.constants,
    priceMin: Number(sanitizePriceMin) || 500000,
    priceMax: Number(sanitizePriceMax) || 0,
    currency: currency || DEFAULT_CURRENCY,
    locale: locale,
  });

  const {
    propertyPrice,
    setPropertyPrice,
    minPropertyPrice,
    maxPropertyPrice,
    propertyPriceStep,
    loanPeriod,
    setLoanPeriod,
    minLoanPeriod,
    maxLoanPeriod,
    loanPeriodStepSize,
    handleSliderChangeLoanPeriod,
    downPayment,
    setDownPayment,
    minDownPayment,
    maxDownPayment,
    downPaymentStepSize,
    handleSliderChangeDownPayment,
    interestRate,
    setInterestRate,
    minInterestRate,
    maxInterestRate,
    interestRateStepSize,
    handleSliderChangeInterestRate,
    resetToDefault,
    handleSliderChange,
    downpaymentAmount,
    formatStringIntoNumber,
    setDefaultPropertyPrice,
    setDefaultDownPayment,
    setDefaultLoanPeriod,
    setDefaultInterestRate,
  } = data;

  const formatPrice = useCallback(
    (price: string | number) =>
      format.number(Math.round(Number(price) / 100 || 0), {
        currency: currency,
        trailingZeroDisplay: "stripIfInteger",
      }),
    [format, currency],
  );

  useEffect(() => {
    const fetchConversionRate = async () => {
      try {
        const conversionRateResponse: any = await search({ currency: getCookie("currency") ?? DEFAULT_CURRENCY });
        if (conversionRateResponse && conversionRateResponse?.conversionrate) {
          setCurrentCurrencyConverstionRate(conversionRateResponse?.conversionrate);
          const amount =
            currentCurrency !== DEFAULT_CURRENCY && conversionRateResponse?.conversionrate === "1"
              ? t("common.loading")
              : format.number(Number(Math.ceil(propertyPrice * conversionRateResponse?.conversionrate)));
          setConvertedPropertyPrice(amount);
        }
      } catch (error) {
        console.error("Error fetching conversion rate:", error);
        setCurrentCurrencyConverstionRate(1);
        setConvertedPropertyPrice(propertyPrice);
      }
    };
    if (currentCurrencyConverstionRate === 0 || previousSelectedCurrency !== currentCurrency) {
      setPreviousSelectedCurrency(currentCurrency || DEFAULT_CURRENCY);
      setConvertedPropertyPrice(t("common.loading"));
      setCurrentCurrencyConverstionRate(0);
      fetchConversionRate();
    } else {
      setConvertedPropertyPrice(propertyPrice);
    }
  }, [propertyPrice, currentCurrency]);

  const handleChangePropertyPrice = useCallback(
    (value: string) => {
      // Remove commas from the string
      const stringIntoNumber = value.replace(/,/g, "");

      // Convert the cleaned string to a number
      const numberValue = Number(stringIntoNumber);

      setPropertyPrice(numberValue);
      setConvertedPropertyPrice(numberValue);
    },
    [setPropertyPrice],
  );

  const handleChangeLoanPeriod = useCallback(
    (value: string) => {
      // Remove commas from the string
      const stringIntoNumber = value.replace(/,/g, "");

      // Convert the cleaned string to a number
      const numberValue = Number(stringIntoNumber);

      setLoanPeriod(numberValue);
    },
    [setLoanPeriod],
  );

  const handleChangeDownPayment = useCallback(
    (value: string) => {
      // Remove commas from the string
      const stringIntoNumber = value.replace(/,/g, "");

      // Convert the cleaned string to a number
      const numberValue = Number(stringIntoNumber);

      setDownPayment(numberValue);
    },
    [setDownPayment],
  );

  const handleChangeInterestRate = useCallback(
    (value: string) => {
      // Remove commas from the string
      const stringIntoNumber = value.replace(/,/g, "");

      // Convert the cleaned string to a number
      const numberValue = Number(stringIntoNumber);

      setInterestRate(numberValue);
    },
    [setInterestRate],
  );

  // if (projectInfo?.country?.toLowerCase() !== allowedCountry?.toLowerCase() || projectInfo?.status?.toLowerCase() !== allowedStatus?.toLowerCase()) {
  //   return null;
  // }

  return (
    <div className="container mb-xxl mt-xxl" id="mortgage">
      <div className="flex justify-center flex-col items-center mb-xl">
        {entry?.title && entry?.typeIndex === 1 ? (
          <h1 className="text-xl font-primary mb-xs uppercase">{entry?.title}</h1>
        ) : (
          <h2 className="text-xl font-primary mb-xs uppercase">{entry?.title}</h2>
        )}
        {entry?.description && <p className="text-m font-secondary mb-xxs">{entry?.description}</p>}
      </div>
      <div className="border border-gray-10 bg-white shadow-strong rounded-lg overflow-hidden flex flex-wrap text-black">
        <div className="bg-white p-l md:w-3/4 w-full">
          <div className="grid gap-xl grid-cols-1 sm:grid-cols-2">
            <div className="w-full">
              <MortgageCalculatorLabelInput
                label={entry?.fields[0]?.title || "Property Price"}
                icon="warning.svg"
                iconTitle="Warning"
                inputName="propertyPrice"
                inputValue={convertedPropertyPrice}
                // handleChange={(value: number) => setPropertyPrice(value)}
                handleChange={handleChangePropertyPrice}
                setDefaultValue={setDefaultPropertyPrice}
                inputLabel={currency || currentCurrency}
              />
              <div>
                <RangeSlider
                  className="guided-tool-range-slide slide-dark my-l"
                  min={minPropertyPrice}
                  max={maxPropertyPrice}
                  step={propertyPriceStep}
                  defaultValue={[0, formatStringIntoNumber(propertyPrice)]}
                  value={[0, formatStringIntoNumber(propertyPrice)]}
                  thumbsDisabled={[true, false]}
                  rangeSlideDisabled={true}
                  onInput={handleSliderChange}
                />
              </div>
            </div>
            <div className="w-full">
              <MortgageCalculatorLabelInput
                label={entry?.fields[1]?.title || "Loan Period"}
                iconTitle="Warning"
                inputName="loanPeriod"
                inputValue={loanPeriod}
                // handleChange={(value: number) => setLoanPeriod(value)}
                handleChange={handleChangeLoanPeriod}
                setDefaultValue={setDefaultLoanPeriod}
                inputLabel={t("label.common.years")}
              />
              <div>
                <RangeSlider
                  className="guided-tool-range-slide slide-dark my-l"
                  min={minLoanPeriod}
                  max={maxLoanPeriod}
                  step={loanPeriodStepSize}
                  defaultValue={[0, loanPeriod]}
                  value={[0, loanPeriod]}
                  thumbsDisabled={[true, false]}
                  rangeSlideDisabled={true}
                  onInput={handleSliderChangeLoanPeriod}
                />
              </div>
            </div>
          </div>
          <div className="grid gap-xl grid-cols-1 sm:grid-cols-2">
            <div className="w-full">
              <MortgageCalculatorLabelInput
                label={entry?.fields[2]?.title || "Down Payment"}
                iconTitle="Warning"
                inputName="downPayment"
                inputValue={downPayment}
                // inputValue={downpaymentAmount}
                // handleChange={(value: number) => setDownPayment(value)}
                handleChange={handleChangeDownPayment}
                setDefaultValue={setDefaultDownPayment}
                inputLabel={`%`}
                // inputLabel={`Years ${downPayment}%`}
              />
              <div>
                <RangeSlider
                  className="guided-tool-range-slide slide-dark my-l"
                  min={minDownPayment}
                  max={maxDownPayment}
                  step={downPaymentStepSize}
                  defaultValue={[0, downPayment]}
                  value={[0, downPayment]}
                  thumbsDisabled={[true, false]}
                  rangeSlideDisabled={true}
                  onInput={handleSliderChangeDownPayment}
                />
              </div>
            </div>
            <div className="w-full">
              <MortgageCalculatorLabelInput
                label={`${entry?.fields[3]?.title} *` || "Interest Rate"}
                iconTitle="Warning"
                inputName="interestRate"
                inputValue={interestRate}
                // handleChange={(value: number) => setInterestRate(value)}
                handleChange={handleChangeInterestRate}
                setDefaultValue={setDefaultInterestRate}
                inputLabel="%"
              />
              <div>
                <RangeSlider
                  className="guided-tool-range-slide slide-dark my-l"
                  min={minInterestRate}
                  max={maxInterestRate}
                  step={interestRateStepSize}
                  defaultValue={[0, interestRate]}
                  value={[0, interestRate]}
                  thumbsDisabled={[true, false]}
                  rangeSlideDisabled={true}
                  onInput={handleSliderChangeInterestRate}
                />
              </div>
            </div>
          </div>
          <Button className="flex items-center justify-center w-full" onClick={resetToDefault}>
            <Icon className="stroke-black stroke-2 mr-xs ml-xxs" name="reset.svg" size="16" title="" />
            <span className="text-s font-secondary font-medium">{t("label.common.resettodefault")}</span>
          </Button>
        </div>
        <MortgageCalculatorLoanAmountSection
          label="Loan Amount"
          icon="warning.svg"
          mortgageCalculatorData={entry}
          currentCurrency={currency || currentCurrency}
          locale={locale}
          currentCurrencyConverstionRate={currentCurrencyConverstionRate}
          {...data}
        />
      </div>
    </div>
  );
}
